import React, { useState, useEffect, useMemo } from 'react';
import './ShoppingListTab.css';
import { apiGet, apiPost, apiPatch, apiDelete } from '../utils/api';

const ShoppingListTab = ({ currentUser }) => {
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchShoppingList = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await apiGet('/shopping-list', { auth: true });
        setItems(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchShoppingList();
  }, [currentUser]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const item of items) {
      const group = map.get(item.name) ?? { name: item.name, ids: [], allChecked: true, anyChecked: false };
      group.ids.push(item.id);
      group.allChecked = group.allChecked && item.checked;
      group.anyChecked = group.anyChecked || item.checked;
      map.set(item.name, group);
    }
    return [...map.values()];
  }, [items]);

  const handleAddItem = async (e) => {
    e.preventDefault();

    if (!newItem.trim() || !currentUser) return;

    try {
      const created = await apiPost('/shopping-list', { name: newItem.trim() }, { auth: true });
      setItems((prev) => [...prev, created]);
      setNewItem('');
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleGroup = async (group) => {
    if (!currentUser) return;

    const nextChecked = !group.allChecked;
    const results = await Promise.allSettled(
      group.ids.map((id) => apiPatch(`/shopping-list/${id}`, { checked: nextChecked }, { auth: true }))
    );

    const succeededIds = new Set(
      group.ids.filter((_, index) => results[index].status === 'fulfilled')
    );

    setItems((prev) =>
      prev.map((item) => (succeededIds.has(item.id) ? { ...item, checked: nextChecked } : item))
    );
  };

  const deleteGroup = async (group) => {
    if (!currentUser) return;

    const results = await Promise.allSettled(
      group.ids.map((id) => apiDelete(`/shopping-list/${id}`, { auth: true }))
    );

    const succeededIds = new Set(
      group.ids.filter((_, index) => results[index].status === 'fulfilled')
    );

    setItems((prev) => prev.filter((item) => !succeededIds.has(item.id)));
  };

  const clearCompleted = async () => {
    if (!currentUser) return;

    const checkedIds = items.filter((item) => item.checked).map((item) => item.id);
    const results = await Promise.allSettled(
      checkedIds.map((id) => apiDelete(`/shopping-list/${id}`, { auth: true }))
    );

    const succeededIds = new Set(
      checkedIds.filter((_, index) => results[index].status === 'fulfilled')
    );

    setItems((prev) => prev.filter((item) => !succeededIds.has(item.id)));
  };

  if (loading) {
    return <div className="loading">Loading shopping list...</div>;
  }

  return (
    <div className="shopping-list-tab">
      <h2>Shopping List</h2>

      {error && <div className="error">{error}</div>}

      <form onSubmit={handleAddItem} className="add-item-form">
        <input
          type="text"
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          placeholder="Add an item to your shopping list..."
          className="item-input"
        />
        <button type="submit" className="add-btn">Add Item</button>
      </form>

      {grouped.length > 0 ? (
        <>
          <div className="list-controls">
            <button
              onClick={clearCompleted}
              className="clear-completed-btn"
            >
              Clear Completed
            </button>
          </div>

          <ul className="shopping-list">
            {grouped.map((group) => (
              <li key={group.name} className={`list-item ${group.allChecked ? 'checked' : ''}`}>
                <label className="item-label">
                  <input
                    type="checkbox"
                    checked={group.allChecked}
                    onChange={() => toggleGroup(group)}
                    className="item-checkbox"
                  />
                  <span className="item-name">{group.name}</span>
                </label>
                <span className="item-quantity">Qty: {group.ids.length}</span>
                <button
                  onClick={() => deleteGroup(group)}
                  className="delete-btn"
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="empty-list">Your shopping list is empty. Add some items to get started!</p>
      )}
    </div>
  );
};

export default ShoppingListTab;
